import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type CrmViewMode = 'table' | 'tiles';

export type CrmColumnVisibilityMap = Record<string, boolean>;

interface CrmViewPreferences {
  viewMode?: CrmViewMode;
}

const DEFAULT_VIEW_MODE: CrmViewMode = 'table';

const STORAGE_KEY_PREFIXES = {
  goods: {
    view: 'orglines.goods.view',
    columns: 'orglines.goods.columns',
  },
  salesOrders: {
    view: 'orglines.sales-orders.view',
    columns: 'orglines.sales-orders.columns',
  },
} as const;

type CrmPage = keyof typeof STORAGE_KEY_PREFIXES;
type CrmArea = keyof (typeof STORAGE_KEY_PREFIXES)[CrmPage];

@Injectable({ providedIn: 'root' })
export class CrmViewPreferencesService {
  private readonly platformId = inject(PLATFORM_ID);

  public getViewMode(companyId: string): CrmViewMode {
    const prefs = this.readPreferences(this.buildKey('goods', 'view', companyId));
    return this.coerceViewMode(prefs.viewMode);
  }

  public setViewMode(companyId: string, mode: CrmViewMode): void {
    const storageKey = this.buildKey('goods', 'view', companyId);
    const prefs = this.readPreferences(storageKey);
    this.writePreferences(storageKey, { ...prefs, viewMode: mode });
  }

  public getColumnVisibility(companyId: string): CrmColumnVisibilityMap {
    return this.readColumnVisibility(this.buildKey('goods', 'columns', companyId));
  }

  public setColumnVisibility(
    companyId: string,
    map: CrmColumnVisibilityMap,
  ): void {
    this.writeColumnVisibility(this.buildKey('goods', 'columns', companyId), map);
  }

  public getSalesOrderViewMode(companyId: string): CrmViewMode {
    const prefs = this.readPreferences(
      this.buildKey('salesOrders', 'view', companyId),
    );
    return this.coerceViewMode(prefs.viewMode);
  }

  public setSalesOrderViewMode(
    companyId: string,
    mode: CrmViewMode,
  ): void {
    const storageKey = this.buildKey('salesOrders', 'view', companyId);
    const prefs = this.readPreferences(storageKey);
    this.writePreferences(storageKey, { ...prefs, viewMode: mode });
  }

  public getSalesOrderColumnVisibility(
    companyId: string,
  ): CrmColumnVisibilityMap {
    return this.readColumnVisibility(
      this.buildKey('salesOrders', 'columns', companyId),
    );
  }

  public setSalesOrderColumnVisibility(
    companyId: string,
    map: CrmColumnVisibilityMap,
  ): void {
    this.writeColumnVisibility(
      this.buildKey('salesOrders', 'columns', companyId),
      map,
    );
  }

  private buildKey(page: CrmPage, area: CrmArea, companyId: string): string {
    return `${STORAGE_KEY_PREFIXES[page][area]}.${companyId}`;
  }

  private coerceViewMode(value: unknown): CrmViewMode {
    return value === 'tiles' || value === 'table' ? value : DEFAULT_VIEW_MODE;
  }

  private readPreferences(
    storageKey: string,
  ): CrmViewPreferences {
    const storage = this.tryGetStorage();
    if (!storage) {
      return {};
    }

    try {
      const raw = storage.getItem(storageKey);
      if (!raw) {
        return {};
      }
      const parsed: unknown = JSON.parse(raw);
      return this.isPreferencesShape(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private writePreferences(
    storageKey: string,
    prefs: CrmViewPreferences,
  ): void {
    const storage = this.tryGetStorage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(storageKey, JSON.stringify(prefs));
    } catch {
      // Swallow quota / serialization errors and fall back to in-memory defaults.
    }
  }

  private readColumnVisibility(
    storageKey: string,
  ): CrmColumnVisibilityMap {
    const storage = this.tryGetStorage();
    if (!storage) {
      return {};
    }

    try {
      const raw = storage.getItem(storageKey);
      if (!raw) {
        return {};
      }
      const parsed: unknown = JSON.parse(raw);
      return this.coerceColumnVisibility(parsed);
    } catch {
      return {};
    }
  }

  private writeColumnVisibility(
    storageKey: string,
    map: CrmColumnVisibilityMap,
  ): void {
    const storage = this.tryGetStorage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(storageKey, JSON.stringify(map));
    } catch {
      // Swallow quota / serialization errors and fall back to in-memory defaults.
    }
  }

  private tryGetStorage(): Storage | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      return typeof window !== 'undefined' ? window.localStorage : null;
    } catch {
      return null;
    }
  }

  private isPreferencesShape(value: unknown): value is CrmViewPreferences {
    return typeof value === 'object' && value !== null;
  }

  private coerceColumnVisibility(value: unknown): CrmColumnVisibilityMap {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return {};
    }

    const result: CrmColumnVisibilityMap = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (typeof entry === 'boolean') {
        result[key] = entry;
      }
    }
    return result;
  }
}
